"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { ChevronUp, MessageSquare, PenSquare, ShieldCheck } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { forumService, formatScore, timeAgo, forumPath } from "./ForumService";
import { useForumAccess } from "../hooks/useForumAccess";
import { ForumSearchBox } from "./ForumSearchBox";
import { StaffBadge } from "./ForumBadges";

// Top of the CRM forum: identity, your rep, the New post button, and THE
// BOARD — the sales-floor whiteboard as a strip of monospaced numbers, with
// the thread of the month. No hero block; the content is the hero.
export function ForumHero() {
  const router = useRouter();
  const { eligibility, requireWrite, isModerator } = useForumAccess();
  const { data: board } = useQuery({
    queryKey: ["forum", "board"],
    queryFn: forumService.getBoard,
    staleTime: 5 * 60_000,
  });
  const { data: modStats } = useQuery({
    queryKey: ["forum", "mod", "stats"],
    queryFn: forumService.mod.getStats,
    enabled: isModerator,
    staleTime: 60_000,
  });

  const go = (category?: string) =>
    requireWrite(() => router.push(forumPath(`/submit${category ? `?category=${category}` : ""}`)));

  const stats: Array<[number, string]> = board
    ? [
        [board.threads, board.threads === 1 ? "thread" : "threads"],
        [board.replies, board.replies === 1 ? "reply" : "replies"],
        [board.wins, board.wins === 1 ? "win posted" : "wins posted"],
        [board.caseHelp, board.caseHelp === 1 ? "case answered" : "cases answered"],
        [board.agents, board.agents === 1 ? "agent on the floor" : "agents on the floor"],
      ].filter(([n]) => (n as number) > 0) as Array<[number, string]>
    : [];

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">InsuraCentral Community</p>
          <h1 className="forum-serif mt-1 text-[24px] font-medium leading-none tracking-tight text-foreground sm:text-[26px]">The Producer Floor</h1>
          <p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">
            Every independent InsuraCentral agent, one room. Ask, post the win, help a case.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {eligibility && (
            <div className="hidden items-baseline gap-1.5 rounded-md border bg-card px-3 py-1.5 font-mono text-[12px] text-muted-foreground sm:flex">
              your rep <strong className="text-[14px] font-semibold tabular-nums text-foreground">{formatScore(eligibility.stats?.karma ?? 0)}</strong>
            </div>
          )}
          {isModerator && (
            <Button size="sm" variant="outline" className="h-9 gap-1.5 px-3" onClick={() => router.push(forumPath("/mod"))}>
              <ShieldCheck className="h-3.5 w-3.5" />
              Mod queue
              {!!modStats?.pending_reports && (
                <span className="ml-0.5 rounded-sm bg-red-600 px-1.5 font-mono text-[11px] font-semibold text-white">{modStats.pending_reports}</span>
              )}
            </Button>
          )}
          <Button size="sm" className="h-9 gap-1.5 px-3.5" onClick={() => go()}>
            <PenSquare className="h-3.5 w-3.5" />
            New post
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <ForumSearchBox className="w-full sm:w-64" />
        {[
          { label: "Ask a question", category: "general" },
          { label: "Post a win", category: "wins" },
          { label: "Case help", category: "underwriting" },
          { label: "Drop a script", category: "sales-objections" },
        ].map((a) => (
          <button
            key={a.category}
            type="button"
            onClick={() => go(a.category)}
            className="h-7 rounded-md border bg-card px-2.5 text-[12px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            {a.label}
          </button>
        ))}
      </div>

      {board && (stats.length > 0 || board.featured) && (
        <div className="mt-4 rounded-lg border bg-card">
          {stats.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-3.5 py-2 font-mono text-[12px] text-muted-foreground">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {board.window === "30d" ? "The board · last 30 days" : "The board · all time"}
              </span>
              {stats.map(([n, label]) => (
                <span key={label} className="whitespace-nowrap">
                  <strong className="font-semibold tabular-nums text-foreground">{formatScore(n)}</strong> {label}
                </span>
              ))}
            </div>
          )}
          {board.featured && (
            <Link href={forumPath(`/t/${board.featured.slug}`)} className="group block px-3.5 py-3 transition-colors hover:bg-muted/40">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                Thread of the month · {board.featured.category.name}
              </p>
              <p className="forum-serif mt-1 text-[18px] font-medium leading-snug text-foreground transition-colors group-hover:text-primary sm:text-[20px]">
                {board.featured.title}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 font-mono text-[11px] tabular-nums text-muted-foreground">
                <span>{board.featured.author.name}</span>
                {board.featured.author.isStaff && <StaffBadge />}
                <span className="inline-flex items-center gap-0.5">
                  <ChevronUp className="h-3 w-3 text-primary" strokeWidth={2.5} />
                  {formatScore(board.featured.score)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {formatScore(board.featured.commentCount)}
                </span>
                <span>{timeAgo(board.featured.createdAt)}</span>
              </p>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
